import * as XLSX from "xlsx";
import { format } from "date-fns";

export const exportToExcel = ({ utilizationData, dateRangeType, customDateRange }) => {
  try {
    // Get hourly utilization data
    const hourlyData =
      utilizationData.find((item) => item.type === "HourlyUtilization")?.data || [];

    // Get floor data
    const floorData =
      utilizationData.find((item) => item.type === "Floors")?.data || [];

    // Get department data
    const departmentData =
      utilizationData.find((item) => item.type === "DepartmentUtilization")?.data || [];

    // Get zone data
    const zoneData =
      utilizationData.find((item) => item.type === "ZoneUtilization")?.data || [];

    // Create workbook
    const wb = XLSX.utils.book_new();

    // 1. Hourly Utilization Summary Sheet
    if (hourlyData.length > 0) {
      const hourlyRows = hourlyData.map((entry) => ({
        Hour: `${entry.hour.toString().padStart(2, "0")}:00`,
        Count: entry.count,
        "Utilization %": entry.utilizationPercentage?.toFixed(2) || 0,
        Zones: entry.zones?.length || 0,
      }));
      const ws1 = XLSX.utils.json_to_sheet(hourlyRows);
      XLSX.utils.book_append_sheet(wb, ws1, "Hourly Summary");
    }

    // 2. Detailed Hourly with Zones Sheet
    if (hourlyData.length > 0) {
      const detailedRows = [];
      hourlyData.forEach((entry) => {
        if (entry.zones && entry.zones.length > 0) {
          entry.zones.forEach((zone) => {
            detailedRows.push({
              Hour: `${entry.hour.toString().padStart(2, "0")}:00`,
              "Hour Total": entry.count,
              "Hour Utilization %": entry.percentage?.toFixed(2) || entry.utilizationPercentage?.toFixed(2) || 0,
              Zone: zone.zoneName,
              "Zone Count": zone.count,
              "Zone Capacity": zone.capacity,
              "Zone Utilization %": zone.percentage?.toFixed(2) || 0,
            });
          });
        }
      });
      if (detailedRows.length > 0) {
        const ws2 = XLSX.utils.json_to_sheet(detailedRows);
        XLSX.utils.book_append_sheet(wb, ws2, "Hourly by Zone");
      }
    }

    // 3. Job Family Groups Sheet
    if (hourlyData.length > 0) {
      const jobFamilyGroupRows = [];
      hourlyData.forEach((entry) => {
        if (entry.zones && entry.zones.length > 0) {
          entry.zones.forEach((zone) => {
            if (zone.jobFamilyGroups && zone.jobFamilyGroups.length > 0) {
              zone.jobFamilyGroups.forEach((group) => {
                jobFamilyGroupRows.push({
                  Hour: `${entry.hour.toString().padStart(2, "0")}:00`,
                  Zone: zone.zoneName,
                  "Job Family Group": group.jobFamilyGroup,
                  Count: group.count,
                  "Percentage": group.percentage?.toFixed(2) || 0,
                  Employees: group.employees?.length || 0,
                });
              });
            }
          });
        }
      });
      if (jobFamilyGroupRows.length > 0) {
        const ws3 = XLSX.utils.json_to_sheet(jobFamilyGroupRows);
        XLSX.utils.book_append_sheet(wb, ws3, "Job Family Groups");
      }
    }

    // 4. Job Families Sheet
    if (hourlyData.length > 0) {
      const jobFamilyRows = [];
      hourlyData.forEach((entry) => {
        if (entry.zones && entry.zones.length > 0) {
          entry.zones.forEach((zone) => {
            if (zone.jobFamilies && zone.jobFamilies.length > 0) {
              zone.jobFamilies.forEach((family) => {
                jobFamilyRows.push({
                  Hour: `${entry.hour.toString().padStart(2, "0")}:00`,
                  Zone: zone.zoneName,
                  "Job Family": family.jobFamily,
                  Count: family.count,
                  "Percentage": family.percentage?.toFixed(2) || 0,
                  Employees: family.employees?.length || 0,
                });
              });
            }
          });
        }
      });
      if (jobFamilyRows.length > 0) {
        const ws4 = XLSX.utils.json_to_sheet(jobFamilyRows);
        XLSX.utils.book_append_sheet(wb, ws4, "Job Families");
      }
    }

    // 5. Employee Details Sheet
    if (hourlyData.length > 0) {
      const employeeRows = [];
      hourlyData.forEach((entry) => {
        if (entry.zones && entry.zones.length > 0) {
          entry.zones.forEach((zone) => {
            // From Job Family Groups
            if (zone.jobFamilyGroups && zone.jobFamilyGroups.length > 0) {
              zone.jobFamilyGroups.forEach((group) => {
                if (group.employees && group.employees.length > 0) {
                  group.employees.forEach((emp) => {
                    employeeRows.push({
                      Hour: `${entry.hour.toString().padStart(2, "0")}:00`,
                      Zone: zone.zoneName,
                      "Job Family Group": group.jobFamilyGroup,
                      "Employee Name": emp.fullName || "N/A",
                      "Job Family": emp.jobFamily || "N/A",
                      Supervisor: emp.supervisorFullName || "N/A",
                      "Expected Zone": emp.expectedZone || "N/A",
                    });
                  });
                }
              });
            }
            // From Job Families (in case they have different employees)
            if (zone.jobFamilies && zone.jobFamilies.length > 0) {
              zone.jobFamilies.forEach((family) => {
                if (family.employees && family.employees.length > 0) {
                  family.employees.forEach((emp) => {
                    // Check if employee already added (avoid duplicates)
                    const exists = employeeRows.some(
                      (row) =>
                        row.Hour === `${entry.hour.toString().padStart(2, "0")}:00` &&
                        row.Zone === zone.zoneName &&
                        row["Employee Name"] === emp.fullName
                    );
                    if (!exists) {
                      employeeRows.push({
                        Hour: `${entry.hour.toString().padStart(2, "0")}:00`,
                        Zone: zone.zoneName,
                        "Job Family Group": "N/A",
                        "Employee Name": emp.fullName || "N/A",
                        "Job Family": emp.jobFamily || "N/A",
                        Supervisor: emp.supervisorFullName || "N/A",
                        "Expected Zone": emp.expectedZone || "N/A",
                      });
                    }
                  });
                }
              });
            }
          });
        }
      });
      if (employeeRows.length > 0) {
        const ws5 = XLSX.utils.json_to_sheet(employeeRows);
        XLSX.utils.book_append_sheet(wb, ws5, "Employee Details");
      }
    }

    // 6. Floor Utilization Sheet
    if (floorData.length > 0) {
      const floorRows = floorData.map((entry) => ({
        Floor: entry.floorName,
        Capacity: entry.capacity,
        Count: entry.count,
        "Utilization %": entry.utilizationPercentage?.toFixed(2) || 0,
      }));
      const ws6 = XLSX.utils.json_to_sheet(floorRows);
      XLSX.utils.book_append_sheet(wb, ws6, "Floor Utilization");
    }

    // 7. Department Utilization Sheet
    if (departmentData.length > 0) {
      const departmentRows = departmentData.map((entry) => ({
        Department: entry.department,
        Count: entry.count,
        "Utilization %": entry.utilizationPercentage?.toFixed(2) || 0,
      }));
      const ws7 = XLSX.utils.json_to_sheet(departmentRows);
      XLSX.utils.book_append_sheet(wb, ws7, "Department Utilization");
    }

    // 8. Zone Utilization Sheet
    if (zoneData.length > 0) {
      const zoneRows = zoneData.map((entry) => ({
        Zone: entry.zoneName,
        Count: entry.count,
        Capacity: entry.capacity || "N/A",
        "Utilization %": entry.utilizationPercentage?.toFixed(2) || 0,
      }));
      const ws8 = XLSX.utils.json_to_sheet(zoneRows);
      XLSX.utils.book_append_sheet(wb, ws8, "Zone Utilization");
    }

    // Generate filename with date
    const dateStr = format(new Date(customDateRange.from), "yyyy-MM-dd");
    const filename = `CubeDEX_Detailed_Report_${dateRangeType}_${dateStr}.xlsx`;

    // Write file
    XLSX.writeFile(wb, filename);

    return true;
  } catch (error) {
    console.error("Error exporting to Excel:", error);
    return false;
  }
};
